/**
 * Layout dos e-mails internos (avisos para o time Nucont sobre uma contabilidade).
 * Cada e-mail é um cabeçalho + blocos de pares rótulo/valor. O bloco destacado
 * fica no topo e serve para o que o time precisa agir primeiro (ex.: quem pediu).
 */

export const escapeHtml = (value) =>
    String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

const isEmpty = (value) => {
    const text = String(value ?? "").trim();
    return !text || text === "undefined" || text === "null";
};

export const valueOrDash = (value) =>
    isEmpty(value) ? "-" : escapeHtml(String(value).trim());

const link = (href, text) =>
    `<a href="${escapeHtml(href)}" style="color: #00a3a9; text-decoration: none;">${escapeHtml(text)}</a>`;

export const whatsappLink = (phone) => {
    const digits = String(phone ?? "").replace(/\D/g, "");
    if (!digits) return "-";
    const withCountry = digits.length <= 11 ? `55${digits}` : digits;
    return link(`https://wa.me/${withCountry}`, phone);
};

export const emailLink = (email) =>
    isEmpty(email) ? "-" : link(`mailto:${String(email).trim()}`, String(email).trim());

/** Valor já em HTML (link, <strong>…) — não passa por escape. */
export const html = (value) => ({ __html: value });

const renderValue = (value) =>
    value && typeof value === "object" && "__html" in value
        ? value.__html
        : valueOrDash(value);

const renderRow = ([label, value]) => `
    <tr>
        <td style="padding: 6px 12px 6px 0; width: 180px; color: #6b8599; font-size: 14px; vertical-align: top;">${escapeHtml(label)}</td>
        <td style="padding: 6px 0; color: #2e5573; font-size: 15px; font-weight: 600; vertical-align: top;">${renderValue(value)}</td>
    </tr>`;

const renderSection = ({ title, rows = [], highlight = false }) => `
    <tr>
        <td style="padding: 0 0 20px 0;">
            <table cellpadding="0" cellspacing="0" border="0" width="100%" style="
                border-collapse: separate;
                border: 1px solid ${highlight ? "#00cbd2" : "#e8e7e6"};
                border-radius: 8px;
                background-color: ${highlight ? "#f0fcfc" : "#ffffff"};
            ">
                <tbody>
                    <tr>
                        <td style="padding: 14px 18px 4px 18px; font-size: 13px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; color: ${
                            highlight ? "#00a3a9" : "#6b8599"
                        };">${escapeHtml(title)}</td>
                    </tr>
                    <tr>
                        <td style="padding: 0 18px 12px 18px;">
                            <table cellpadding="0" cellspacing="0" border="0" width="100%">
                                <tbody>${rows.map(renderRow).join("")}</tbody>
                            </table>
                        </td>
                    </tr>
                </tbody>
            </table>
        </td>
    </tr>`;

/**
 * Converte o usuário logado (formato meteor de `UserDataAdapter().user()`)
 * no `requester` esperado por `requesterSection`.
 */
export const requesterFromUser = (user) => ({
    name: user?.profile?.name || user?.profile?.nickname,
    phone: user?.profile?.whatsapp,
    email: user?.emails?.[0]?.address || user?.username
});

/**
 * Bloco "Registrado por" — operador interno da Nucont que executou a ação.
 * Retorna null (bloco omitido) quando não há nome nem e-mail.
 */
export const registeredBySection = (operator = {}) =>
    isEmpty(operator?.name) && isEmpty(operator?.email)
        ? null
        : {
              title: "Registrado por",
              highlight: true,
              rows: [
                  ["Nome", operator.name],
                  ["E-mail", html(emailLink(operator.email))]
              ]
          };

/**
 * Linha "Integração Domínio": "Sim (N empresas conectadas)" / "Não".
 * Mesma regra do atributo `dominio_integrado` do Chatwoot (ChatwootAdapter).
 * Sem `integrationsHealth` (objeto não carregado) sai "-": não dá para afirmar "Não".
 */
export const dominioIntegrationRow = (integrationsHealth) => {
    if (!integrationsHealth) return ["Integração Domínio", "-"];
    const connected = integrationsHealth?.dominio?.companies?.connected || 0;
    return [
        "Integração Domínio",
        connected
            ? `Sim (${connected} ${
                  connected === 1 ? "empresa conectada" : "empresas conectadas"
              })`
            : "Não"
    ];
};

/** Bloco "Quem solicitou" padrão, a partir do usuário logado. */
export const requesterSection = (requester = {}) => ({
    title: "Quem solicitou",
    highlight: true,
    rows: [
        ["Nome", requester.name],
        ["Telefone / WhatsApp", html(whatsappLink(requester.phone))],
        ["E-mail", html(emailLink(requester.email))]
    ]
});

/**
 * @param {object} params
 * @param {string} params.eyebrow  linha pequena acima do título (tipo do aviso)
 * @param {string} params.title    título (normalmente "(código) Nome da contabilidade")
 * @param {string} [params.intro]  frase curta abaixo do título (HTML permitido)
 * @param {Array<{title: string, rows: Array<[string, any]>, highlight?: boolean}>} params.sections
 */
export const InternalEmailLayout = ({ eyebrow, title, intro, sections }) => `
<table cellpadding="0" cellspacing="0" border="0" width="100%" style="font-family: Open Sans, sans-serif; color: #2e5573; max-width: 640px; margin: 0 auto;">
    <tbody>
        <tr>
            <td style="height: 4px; background-color: #00cbd2; font-size: 0; line-height: 0;">&nbsp;</td>
        </tr>
        <tr>
            <td style="padding: 24px 0 8px 0; text-align: center;">
                <div style="font-size: 13px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: #00a3a9;">
                    ${escapeHtml(eyebrow)}
                </div>
                <h1 style="margin: 8px 0 0 0; font-size: 22px; font-family: Open Sans, sans-serif;">
                    ${escapeHtml(title)}
                </h1>
            </td>
        </tr>
        ${
            intro
                ? `<tr>
            <td style="padding: 0 0 24px 0; text-align: center; font-size: 15px; color: #6b8599;">${intro}</td>
        </tr>`
                : `<tr><td style="height: 16px; font-size: 0; line-height: 0;">&nbsp;</td></tr>`
        }
        ${sections.filter(Boolean).map(renderSection).join("")}
    </tbody>
</table>
`;
